<template>
  <div class="withdraw-page">
    <div class="page-content">
      <!-- 1. Balance Card - Split Accent Design -->
      <div class="balance-section">
        <div class="balance-card" :class="{ loading: isRefreshing }">
          <!-- Left: Gold accent with currency -->
          <div class="balance-accent">
            <span class="currency">฿</span>
          </div>

          <!-- Right: Content area -->
          <div class="balance-content">
            <div class="balance-header">
              <span class="balance-label">ยอดเงินคงเหลือ</span>
              <button
                class="refresh-btn"
                @click="refreshBalance"
                :disabled="isRefreshing"
                :class="{ refreshing: isRefreshing }"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 12C4 16.4 7.6 20 12 20C16.4 20 20 16.4 20 12C20 7.6 16.4 4 12 4C10.3 4 8.7 4.6 7.5 5.7L9 7.2C9.9 6.5 11 6 12 6C15.3 6 18 8.7 18 12C18 15.3 15.3 18 12 18C8.7 18 6 15.3 6 12H4Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <path d="M6 9L2 12L6 15V9Z" fill="currentColor" />
                </svg>
              </button>
            </div>
            <div class="balance-amount">
              <span class="amount">{{ formatNumber(displayBalance) }}</span>
            </div>
            <div class="balance-subtitle">
              {{ isRefreshing ? 'กำลังอัปเดต...' : 'อัพเดทล่าสุด' }}
            </div>
          </div>

          <div v-if="isRefreshing" class="loading-shimmer"></div>
        </div>
      </div>

      <!-- 2. Turnover Section - only show if API returned success -->
      <div class="turnover-section" v-if="hasTurnoverCondition">
        <div class="turnover-card">
          <h3 class="section-title">เงื่อนไขการถอน</h3>

          <!-- Two-column turnover grid -->
          <div class="turnover-grid">
            <!-- ทำเทิร์น (Turnover) -->
            <div class="turnover-item" v-if="targetTurnover > 0">
              <div class="turnover-header">
                <span class="turnover-type">ทำเทิร์น</span>
                <span
                  class="turnover-percent"
                  :class="{ complete: turnoverComplete }"
                >
                  {{ turnoverComplete ? '✓' : turnoverPercentage + '%' }}
                </span>
              </div>
              <div class="mini-progress">
                <div
                  class="mini-progress-fill"
                  :class="{ complete: turnoverComplete }"
                  :style="{ width: turnoverPercentage + '%' }"
                ></div>
              </div>
              <div class="turnover-values">
                {{ formatNumber(currentTurnover) }} /
                {{ formatNumber(targetTurnover) }}
              </div>
            </div>

            <!-- ทำยอด (Volume) -->
            <div class="turnover-item" v-if="targetVolume > 0">
              <div class="turnover-header">
                <span class="turnover-type">ทำยอด</span>
                <span
                  class="turnover-percent"
                  :class="{ complete: volumeComplete }"
                >
                  {{ volumeComplete ? '✓' : volumePercentage + '%' }}
                </span>
              </div>
              <div class="mini-progress">
                <div
                  class="mini-progress-fill"
                  :class="{ complete: volumeComplete }"
                  :style="{ width: volumePercentage + '%' }"
                ></div>
              </div>
              <div class="turnover-values">
                {{ formatNumber(currentVolume) }} /
                {{ formatNumber(targetVolume) }}
              </div>
            </div>
          </div>

          <!-- Collapsible Promotions -->
          <div class="promo-section" v-if="activePromotions.length > 0">
            <div class="promo-header" @click="promoExpanded = !promoExpanded">
              <span>📎 โปรโมชั่นที่ใช้ ({{ activePromotions.length }})</span>
              <span class="expand-icon">{{ promoExpanded ? '▲' : '▼' }}</span>
            </div>
            <div class="promo-list" v-show="promoExpanded">
              <div
                class="promo-item"
                v-for="promo in activePromotions"
                :key="promo.id"
              >
                <span class="promo-title">{{ promo.title }}</span>
              </div>
            </div>
          </div>

          <!-- Maximum Withdraw Limit -->
          <div class="withdraw-limit-info">
            <span v-if="maximumWithdraw === 0">ถอนได้ไม่จำกัดจำนวน</span>
            <span v-else
              >จำกัดยอดถอนสูงสุด {{ formatNumber(maximumWithdraw) }} บาท</span
            >
          </div>

          <!-- Warning: Credit will be deducted -->
          <div
            class="withdraw-warning mb-1"
            v-if="maximumWithdraw > 0 && canWithdraw"
          >
            กรุณาถอนเงินทั้งหมด เนื่องจากมีการรับโปรที่จำกัดยอดถอน
            มิเช่นนั้นเครดิตจะถูกรีเซ็ต เมื่อฝากเงินครั้งหน้าสามารถ
            ถอนได้ไม่จำกัด
          </div>

          <!-- Status Message -->
          <div
            class="turnover-status"
            :class="{ complete: canWithdraw, warning: !canWithdraw }"
            v-if="targetTurnover > 0 || targetVolume > 0"
          >
            <span v-if="canWithdraw">✅ ถึงเป้าหมายแล้ว! พร้อมถอน</span>
            <span v-else>⚠️ เงื่อนไขในการถอนเงินยังไม่ครบ</span>
          </div>
        </div>
      </div>

      <!-- Payment maintenance notice -->
      <div
        class="withdraw-warning maintenance-banner mb-1"
        v-if="maintenanceActive"
      >
        <div>
          ⚠️ ระบบถอนเงินปิดปรับปรุงชั่วคราว ({{ maintenance.start }}-{{
            maintenance.end
          }}
          น.)
        </div>
        <div v-if="maintenanceCountdownText">
          จะเปิดให้บริการอีกครั้งในอีก {{ maintenanceCountdownText }}
        </div>
        <div>ขออภัยในความไม่สะดวกค่ะ</div>
      </div>

      <!-- 3. Withdrawal Form -->
      <div class="withdraw-form-section">
        <div class="withdraw-form-card">
          <h3 class="section-title">จำนวนเงินถอน</h3>

          <validation-observer ref="withdrawForm" #default="{ invalid }">
            <!-- Amount Input -->
            <div class="form-group">
              <validation-provider
                #default="{ errors }"
                rules="required|integer"
                name="Amount"
              >
                <div
                  class="amount-input-wrapper"
                  :class="{ error: errors.length > 0 }"
                >
                  <span class="currency-prefix">฿</span>
                  <input
                    v-model="amount"
                    type="number"
                    class="amount-input"
                    placeholder="0"
                    :disabled="maximumWithdraw > 0 && canWithdraw"
                  />
                </div>
                <small class="error-text" v-if="errors.length > 0">{{
                  errors[0]
                }}</small>
              </validation-provider>
            </div>

            <!-- Quick Amount Buttons - Hidden when forced withdrawal -->
            <div
              class="quick-amounts"
              v-if="!(maximumWithdraw > 0 && canWithdraw)"
            >
              <button
                type="button"
                @click="setAmount(100)"
                :class="{ active: amount == 100 }"
              >
                100
              </button>
              <button
                type="button"
                @click="setAmount(500)"
                :class="{ active: amount == 500 }"
              >
                500
              </button>
              <button
                type="button"
                @click="setAmount(1000)"
                :class="{ active: amount == 1000 }"
              >
                1,000
              </button>
              <button
                type="button"
                @click="setAmount(Math.floor(displayBalance))"
                class="max-btn"
              >
                ทั้งหมด
              </button>
            </div>

            <!-- Submit Button -->
            <button
              type="button"
              class="withdraw-btn"
              :class="{ disabled: invalid || submitDisabled }"
              :disabled="invalid || submitDisabled"
              @click="withdraw"
            >
              {{
                isSubmitting
                  ? 'กำลังดำเนินการ...'
                  : !turnoverLoaded
                  ? 'กำลังตรวจสอบ...'
                  : maintenanceActive
                  ? 'ปิดปรับปรุงชั่วคราว'
                  : isMockCredit
                  ? 'กรุณายืนยันบัญชี'
                  : !canWithdraw
                  ? 'เงื่อนไขยังไม่ครบ'
                  : 'ถอนเงิน'
              }}
            </button>
          </validation-observer>
        </div>
      </div>

      <!-- 4. Transfer Account Info Section -->
      <div class="transfer-info-section">
        <div class="section-header">
          <span class="header-icon">🏦</span>
          <span class="header-text">บัญชีรับเงิน</span>
        </div>
        <div class="bank-card">
          <div class="bank-icon">
            <feather-icon icon="CreditCardIcon" size="24" />
          </div>
          <div class="bank-info">
            <div class="bank-name">{{ member && member.name_th }}</div>
            <div class="account-number">
              {{ member ? member.bank_no : '•••-•-•••••-•' }}
            </div>
          </div>
        </div>
        <div class="info-notice">
          กรุณาตรวจสอบเลขบัญชีก่อนทำรายการถอนเงิน
          หากเลขบัญชีไม่ถูกต้องกรุณาแก้ไขข้อมูลบัญชีหน้า
          <router-link to="/personal-info" class="info-link"
            >ข้อมูลส่วนตัว</router-link
          >
        </div>
      </div>

      <!-- 5. History List -->
      <div class="history-section">
        <div class="history-card">
          <h3 class="section-title">ประวัติการถอน</h3>

          <div class="history-list" v-if="displayItems.length > 0">
            <div
              v-for="(item, index) in displayItems"
              :key="item.Tx"
              class="history-item"
            >
              <div class="history-index">{{ index + 1 }}</div>
              <div class="history-info">
                <div class="history-amount">
                  {{ formatNumber(item.amount) }} บาท
                </div>
                <div class="history-date">{{ item.date_time }}</div>
              </div>
              <div class="history-status" :class="item.status.variant">
                {{ getStatusText(item.status.title) }}
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <span class="empty-icon">📋</span>
            <span class="empty-text">ไม่มีรายการ</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, integer } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getUserData } from '@/auth/utils'

export default {
  name: 'WithdrawPage',
  components: {
    ValidationObserver,
    ValidationProvider
  },
  data() {
    return {
      amount: '',
      items: [],
      user: null,
      member: null,
      // ทำเทิร์น (Turnover)
      currentTurnover: 0,
      targetTurnover: 0,
      // ทำยอด (Volume)
      currentVolume: 0,
      targetVolume: 0,
      // Turnover API status
      hasTurnoverCondition: false,
      // Maximum withdraw limit
      maximumWithdraw: 0,
      // Promotions
      promoExpanded: false,
      activePromotions: [],
      isRefreshing: false,
      turnoverLoaded: false,
      isSubmitting: false,
      // Payment maintenance window ({ active, start, end, endsAt, startsAt }
      // from getMemberTurnover); `now` ticks so the banner flips at boundaries
      maintenance: null,
      now: Date.now(),
      maintenanceTimer: null,
      required,
      integer
    }
  },
  computed: {
    ...mapState('member', ['credit', 'isMockCredit']),
    displayBalance() {
      return this.$store.getters['member/displayBalance']
    },
    // ทำเทิร์น
    turnoverPercentage() {
      if (!this.targetTurnover || this.targetTurnover === 0) return 100
      const percentage = (this.currentTurnover / this.targetTurnover) * 100
      return Math.min(Math.round(percentage), 100)
    },
    turnoverComplete() {
      return (
        this.targetTurnover === 0 || this.currentTurnover >= this.targetTurnover
      )
    },
    // ทำยอด
    volumePercentage() {
      if (!this.targetVolume || this.targetVolume === 0) return 100
      const percentage = (this.currentVolume / this.targetVolume) * 100
      return Math.min(Math.round(percentage), 100)
    },
    volumeComplete() {
      return this.targetVolume === 0 || this.currentVolume >= this.targetVolume
    },
    // Can withdraw check - if no turnover condition, allow withdraw
    canWithdraw() {
      if (!this.hasTurnoverCondition) return true
      return this.turnoverComplete && this.volumeComplete
    },
    // Payment maintenance: server flag is authoritative, but the client flips
    // at the window boundaries while the page stays open (Cashback idiom)
    maintenanceActive() {
      if (!this.maintenance) return false
      if (this.maintenance.active) {
        return this.maintenance.endsAt
          ? this.now < this.maintenance.endsAt
          : true
      }
      return (
        !!this.maintenance.startsAt && this.now >= this.maintenance.startsAt
      )
    },
    maintenanceCountdownText() {
      if (!this.maintenanceActive || !this.maintenance.endsAt) return ''
      const remainingMs = this.maintenance.endsAt - this.now
      if (remainingMs <= 0) return ''
      const totalMinutes = Math.ceil(remainingMs / 60000)
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return hours > 0 ? `${hours} ชม. ${minutes} นาที` : `${minutes} นาที`
    },
    // `invalid` (validation-observer slot prop) stays in the template
    submitDisabled() {
      return (
        this.maintenanceActive ||
        this.isMockCredit ||
        !this.canWithdraw ||
        !this.turnoverLoaded ||
        this.isSubmitting
      )
    },
    remaining() {
      return Math.max(this.targetTurnover - this.currentTurnover, 0)
    },
    displayItems() {
      return this.items.slice(0, 5)
    }
  },
  watch: {
    // The forced withdraw-all amount and volume progress are derived from the
    // live balance; the input is disabled in forced mode, so overwriting
    // `amount` never clobbers user input
    displayBalance(balance) {
      if (!this.hasTurnoverCondition) return
      this.currentVolume = balance || 0
      this.syncForcedAmount()
    }
  },
  async beforeMount() {
    this.user = getUserData()

    // Credit must be fresh BEFORE getTurnOver(): the forced withdraw-all
    // amount and currentVolume are computed from displayBalance
    await this.fetchCredit({ force: true })

    const [, , , member] = await Promise.all([
      this.checkFreePromo(),
      this.getTransactions(),
      this.getTurnOver(),
      this.getMemberInfo()
    ])

    this.$eventBus.$emit('refreshBalance')
    this.member = member
  },
  mounted() {
    // Tick the clock so the maintenance banner flips at window boundaries;
    // on a flip, re-fetch to reconfirm with the server (which stays
    // authoritative — a stale submit is rejected by the backend anyway)
    this.maintenanceTimer = setInterval(() => {
      const prev = this.maintenanceActive
      this.now = Date.now()
      if (this.maintenance && prev !== this.maintenanceActive)
        this.getTurnOver()
    }, 30000)
  },
  beforeDestroy() {
    if (this.maintenanceTimer) clearInterval(this.maintenanceTimer)
  },
  methods: {
    ...mapActions('member', ['fetchCredit', 'checkFreePromo']),
    async refreshBalance() {
      this.isRefreshing = true
      try {
        await this.fetchCredit({ force: true })
      } catch (error) {
        console.error('Error refreshing balance:', error)
      }
      setTimeout(() => {
        this.isRefreshing = false
      }, 800)
    },
    formatNumber(num) {
      if (!num) return '0'
      return new Intl.NumberFormat('th-TH').format(num)
    },
    setAmount(value) {
      this.amount = value
    },
    // Force withdraw-all when a limit exists and the user can withdraw:
    // balance if less than max, otherwise max. Remaining credit is deducted
    // by the backend
    syncForcedAmount() {
      if (this.maximumWithdraw > 0 && this.canWithdraw) {
        this.amount = Math.min(
          Math.floor(this.displayBalance),
          Math.floor(this.maximumWithdraw)
        )
      }
    },
    getStatusText(status) {
      const statusMap = {
        confirmed: 'สำเร็จ',
        pending: 'รอดำเนินการ',
        reject: 'ยกเลิก'
      }
      return statusMap[status] || status
    },
    getStatusIcon(variant) {
      const iconMap = {
        success: '✅',
        warning: '⏳',
        danger: '❌'
      }
      return iconMap[variant] || '•'
    },
    transformT(txs) {
      const resp = []
      txs.forEach(element => {
        let v = 'warning'
        if (element.status === 'confirmed') {
          v = 'success'
        } else if (element.status === 'reject') {
          v = 'danger'
        }

        resp.push({
          Tx: element.id,
          date_time: `${element.date} ${element.time}`,
          amount: element.amount,
          status: {
            title: element.status,
            variant: v
          }
        })
      })
      return resp
    },
    async getTurnOver() {
      try {
        const resp = await this.$http.post('member/getMemberTurnover')
        this.maintenance = resp.data.paymentMaintenance || null
        if (resp.data.success) {
          this.hasTurnoverCondition = true
          // ทำเทิร์น (Turnover)
          this.currentTurnover = resp.data.data.currentAllTurn || 0
          this.targetTurnover = resp.data.data.targetTurnOver || 0
          // ทำยอด (Volume) - current is user credit, target is targetTurnProfit
          this.currentVolume = this.displayBalance || 0
          this.targetVolume = resp.data.data.targetTurnProfit || 0
          // Maximum withdraw limit
          this.maximumWithdraw = resp.data.data.maximumWithdraw || 0
          this.syncForcedAmount()
          // Promotion - single item from API
          if (resp.data.promotion) {
            this.activePromotions = [{ id: 1, title: resp.data.promotion }]
          } else {
            this.activePromotions = []
          }
        } else {
          // API returned success: false - no turnover condition, allow withdraw
          this.hasTurnoverCondition = false
        }
      } catch (error) {
        console.error('Error fetching turnover:', error)
        this.hasTurnoverCondition = false
      } finally {
        this.turnoverLoaded = true
      }
    },
    async getTransactions() {
      try {
        const tx = await this.$http.get('/member/withdraw/tx')
        if (tx.status === 200) {
          const res = this.transformT(tx.data)
          this.items = res
        }
      } catch (error) {
        console.error('Error fetching transactions:', error)
      }
    },
    showAlert(text) {
      this.$swal({
        text,
        customClass: {
          confirmButton: 'btn btn-primary'
        },
        buttonsStyling: false
      })
    },
    async getMemberInfo() {
      const response = await this.$http.get('member/personal/info')
      if (response.status === 200 && response.data.member) {
        return response.data.member
      }
      return null
    },
    async processWithdraw(amount) {
      try {
        const resp = await this.$http.post('/member/withdraw/v2', {
          amount
        })
        if (resp.status === 200) {
          if (resp.data.success) {
            await this.getTransactions()
            await this.fetchCredit({ force: true })
            this.amount = ''
            this.$toast({
              component: ToastificationContent,
              props: {
                title: resp.data.message,
                icon: 'EditIcon',
                variant: 'success'
              }
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: resp.data.message,
                icon: 'EditIcon',
                variant: 'danger'
              }
            })
          }
        }
      } catch (error) {
        console.error('Error processing withdraw:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาดในการถอนเงิน',
            icon: 'EditIcon',
            variant: 'danger'
          }
        })
      }
    },
    async withdraw() {
      // Check turnover conditions first
      if (!this.canWithdraw) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เงื่อนไขในการถอนเงินยังไม่ครบ',
            icon: 'EditIcon',
            variant: 'danger'
          }
        })
        return
      }

      // Check maximum withdraw limit
      if (this.maximumWithdraw > 0 && this.amount > this.maximumWithdraw) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `ยอดถอนสูงสุดไม่เกิน ${this.formatNumber(
              this.maximumWithdraw
            )} บาท`,
            icon: 'EditIcon',
            variant: 'danger'
          }
        })
        return
      }

      // Prevent multiple clicks
      if (this.isSubmitting) return
      this.isSubmitting = true

      try {
        await this.fetchCredit({ force: true })
        // Re-derive the forced amount from the fresh credit before posting
        this.syncForcedAmount()
        if (this.credit && this.credit.ori.code === 0) {
          if (this.credit.result.credit >= this.amount) {
            if (this.amount > 0) {
              await this.processWithdraw(this.amount)
            } else {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'กรุณาระบุจำนวนเงินที่ต้องการถอน',
                  icon: 'EditIcon',
                  variant: 'danger'
                }
              })
            }
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'จำนวนเครดิตคงเหลือไม่เพียงพอ',
                icon: 'EditIcon',
                variant: 'danger'
              }
            })
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ไม่สามารถตรวจสอบเครดิตคงเหลือได้กรุณาลองอีกครั้ง',
              icon: 'EditIcon',
              variant: 'danger'
            }
          })
        }
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.withdraw-page {
  width: 100%;
  min-height: 100vh;
  padding: 12px;
}

.page-content {
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

// Section Title
.section-title {
  font-size: 1.1rem;
  color: #ffffff;
  margin-bottom: 10px;
  font-weight: 600;
}

// ==========================================
// 1. Balance Card - Split Accent Design
// ==========================================
.balance-section {
  margin-bottom: 0;
}

.balance-card {
  position: relative;
  display: flex;
  background: linear-gradient(
    135deg,
    rgba(var(--theme-secondary-rgb), 0.08) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  border-radius: 12px;
  border: 1px solid rgba(var(--theme-secondary-rgb), 0.2);
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2),
    0 1px 4px rgba(var(--theme-secondary-rgb), 0.08);
  overflow: hidden;

  &.loading .balance-amount {
    opacity: 0.5;
  }
}

// Gold accent strip with currency
.balance-accent {
  width: 48px;
  min-height: 56px;
  background: linear-gradient(
    180deg,
    var(--theme-secondary-light) 0%,
    var(--theme-secondary) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .currency {
    color: #1a1a2e;
    font-size: 1.4rem;
    font-weight: 700;
  }
}

// Content area
.balance-content {
  flex: 1;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.balance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.balance-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.7rem;
  font-weight: 500;
}

.refresh-btn {
  background: rgba(var(--theme-secondary-rgb), 0.12);
  border: 1px solid rgba(var(--theme-secondary-rgb), 0.25);
  color: #ffffff;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(var(--theme-secondary-rgb), 0.2);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &.refreshing svg {
    animation: spin 1s linear infinite;
  }
}

.balance-amount {
  display: flex;
  align-items: baseline;
  transition: opacity 0.3s ease;

  .amount {
    color: #fff;
    font-size: 1.6rem;
    font-weight: 800;
    letter-spacing: 0.5px;
  }
}

.balance-subtitle {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.65rem;
  text-align: right;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(var(--theme-secondary-rgb), 0.2),
    transparent
  );
  animation: shimmer 1.5s ease-in-out infinite;
  pointer-events: none;
}

// ==========================================
// 2. Turnover Section - Two Column Grid
// ==========================================
.turnover-section {
  margin-bottom: 0;
}

.turnover-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.turnover-grid {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.turnover-item {
  flex: 1;
  min-width: 0;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 10px;
}

.turnover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.turnover-type {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.turnover-percent {
  font-size: 0.8rem;
  font-weight: 700;
  color: #f59e0b;

  &.complete {
    color: #4ade80;
  }
}

.mini-progress {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin-bottom: 6px;
  overflow: hidden;
}

.mini-progress-fill {
  height: 100%;
  background: linear-gradient(
    90deg,
    var(--theme-secondary),
    var(--theme-secondary-light)
  );
  border-radius: 3px;
  transition: width 0.3s ease;

  &.complete {
    background: linear-gradient(90deg, #4ade80, #22c55e);
  }
}

.turnover-values {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
}

// Collapsible Promotions
.promo-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 8px;
  margin-bottom: 8px;
}

.promo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  padding: 4px 0;
  user-select: none;

  &:active {
    opacity: 0.7;
  }
}

.expand-icon {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.5);
}

.promo-list {
  margin-top: 8px;
}

.promo-item {
  background: rgba(var(--theme-secondary-rgb), 0.1);
  border: 1px solid rgba(var(--theme-secondary-rgb), 0.2);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}

.promo-title {
  display: block;
  font-size: 0.8rem;
  color: #ffffff;
  font-weight: 600;
}

.promo-condition {
  display: block;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
}

.withdraw-limit-info {
  font-size: 0.85rem;
  color: rgba(238, 99, 99, 0.6);
  text-align: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 8px;
}

.withdraw-warning {
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 8px;
  padding: 10px;
  margin-top: 8px;
  font-size: 0.85rem;
  color: #f59e0b;
  text-align: center;
  font-weight: 500;
}

.maintenance-banner {
  div + div {
    margin-top: 4px;
  }
}

.turnover-status {
  text-align: center;
  padding: 8px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.8rem;

  &.warning {
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.3);
  }

  &.complete {
    background: rgba(74, 222, 128, 0.15);
    color: #4ade80;
    border: 1px solid rgba(74, 222, 128, 0.3);
  }
}

// ==========================================
// 3. Withdrawal Form
// ==========================================
.withdraw-form-section {
  margin-bottom: 0;
}

.withdraw-form-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.form-group {
  margin-bottom: 10px;
}

.amount-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 2px 12px;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: var(--theme-secondary);
    box-shadow: 0 0 0 2px rgba(var(--theme-secondary-rgb), 0.15);
  }

  &.error {
    border-color: #e74c3c;
  }
}

.currency-prefix {
  color: #ffffff;
  font-size: 1.2rem;
  font-weight: 700;
  margin-right: 6px;
}

.amount-input {
  flex: 1;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 600;
  padding: 8px 0;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  // Hide number input spinners
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  appearance: textfield;
}

.error-text {
  color: #e74c3c;
  font-size: 0.75rem;
  margin-top: 4px;
  display: block;
}

.quick-amounts {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 10px;

  button {
    padding: 10px 6px;
    border: 1px solid rgba(var(--theme-secondary-rgb), 0.3);
    background: rgba(var(--theme-secondary-rgb), 0.1);
    border-radius: 8px;
    color: #ffffff;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--theme-secondary-rgb), 0.2);
      border-color: rgba(var(--theme-secondary-rgb), 0.5);
    }

    &:active {
      transform: scale(0.95);
    }

    &.active {
      background: rgba(var(--theme-secondary-rgb), 0.25);
      border-color: var(--theme-secondary);
    }

    &.max-btn {
      background: var(--theme-gradient-primary);
      color: #ffffff;
      border: none;
      font-weight: 700;

      &:hover {
        box-shadow: 0 4px 12px rgba(var(--theme-primary-rgb), 0.3);
      }
    }
  }
}

.withdraw-btn {
  width: 100%;
  padding: 12px 16px;
  background: var(--theme-gradient-primary);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(.disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(var(--theme-primary-rgb), 0.3);
  }

  &:active:not(.disabled) {
    transform: translateY(0);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// ==========================================
// 4. History List
// ==========================================
.history-section {
  margin-bottom: 12px;
}

.history-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.history-index {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--theme-secondary-rgb), 0.2);
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 600;
  color: #ffffff;
  flex-shrink: 0;
}

.history-info {
  flex: 1;
  min-width: 0;

  .history-amount {
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
  }

  .history-date {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.7rem;
  }
}

.history-status {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  flex-shrink: 0;

  &.success {
    background: rgba(40, 167, 69, 0.2);
    color: #4ade80;
  }

  &.warning {
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }

  &.danger {
    background: rgba(220, 53, 69, 0.2);
    color: #ef4444;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  color: rgba(255, 255, 255, 0.5);

  .empty-icon {
    font-size: 1.8rem;
    margin-bottom: 8px;
    opacity: 0.5;
  }

  .empty-text {
    font-size: 0.85rem;
  }
}

// ==========================================
// 4. Transfer Account Info Section
// ==========================================
.transfer-info-section {
  background: rgba(40, 167, 69, 0.08);
  border: 1px solid rgba(40, 167, 69, 0.25);
  border-radius: 10px;
  padding: 10px;

  .section-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;

    .header-icon {
      font-size: 14px;
    }

    .header-text {
      color: #28a745;
      font-size: 12px;
      font-weight: 600;
    }
  }

  .bank-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;

    .bank-icon {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
      border-radius: 6px;
      color: white;
    }

    .bank-info {
      flex: 1;
      min-width: 0;

      .bank-name {
        color: #28a745;
        font-size: 11px;
        font-weight: 500;
        margin-bottom: 2px;
      }

      .account-number {
        color: #fff;
        font-size: 14px;
        font-family: 'Courier New', monospace;
        font-weight: 600;
        letter-spacing: 0.5px;
      }
    }
  }

  .info-notice {
    margin-top: 8px;
    padding: 8px;
    background: rgba(40, 167, 69, 0.05);
    border-radius: 6px;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;

    .info-link {
      color: #28a745;
      text-decoration: underline;

      &:hover {
        color: #34ce57;
      }
    }
  }
}

// ==========================================
// Animations
// ==========================================
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

// ==========================================
// Responsive - Tablet & Desktop
// ==========================================
@media (min-width: 768px) {
  .withdraw-page {
    padding: 16px;
  }

  .page-content {
    max-width: 480px;
    gap: 12px;
  }

  .balance-accent {
    width: 56px;
  }

  .balance-amount .amount {
    font-size: 1.8rem;
  }

  .turnover-card,
  .withdraw-form-card,
  .history-card {
    padding: 14px 16px;
  }

  .quick-amounts button {
    padding: 12px 10px;
    font-size: 0.9rem;
  }
}

// ==========================================
// Responsive - Small Mobile
// ==========================================
@media (max-width: 360px) {
  .withdraw-page {
    padding: 10px;
  }

  .balance-accent {
    width: 40px;
    min-height: 52px;

    .currency {
      font-size: 1.2rem;
    }
  }

  .balance-amount .amount {
    font-size: 1.4rem;
  }

  .stat-value {
    font-size: 0.95rem;
  }

  .quick-amounts {
    gap: 4px;

    button {
      padding: 8px 4px;
      font-size: 0.8rem;
    }
  }

  .history-item {
    padding: 8px 10px;
  }

  .history-amount {
    font-size: 0.9rem;
  }
}
</style>
